/**
 * What the door's checks read: the camera's real size, how loud the
 * microphone is hearing, and how bright the picture is. The meeting's own
 * media gives the two tracks; everything here is plain measurement over them,
 * and `checks.ts` turns the numbers into words.
 *
 * Browser APIs, checked 22 September 2026:
 * https://developer.mozilla.org/en-US/docs/Web/API/AnalyserNode/getFloatTimeDomainData
 * https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
 */
import { brightnessOf, type CameraReading, levelOf, type MicReading, SILENT_LEVEL } from "./checks";

const LEVEL_EVERY_MS = 100;
const LEVELS_KEPT = 30;
const LIGHT_EVERY_MS = 1000;
/** A postage stamp is enough to average the light in a picture. */
const LIGHT_SIZE = 32;

export interface DoorReadings {
  camera: CameraReading;
  mic: MicReading;
  brightness: number | null;
}

export const NO_READINGS: DoorReadings = {
  camera: { on: true, width: 0, height: 0, problem: null },
  mic: { on: true, loudest: 0, label: "", problem: null, needsClick: false, silentMs: 0 },
  brightness: null,
};

/**
 * Holds the microphone's meter and the light's average while the door is
 * open. It reads the tracks it is given and says nothing about the meeting.
 */
export class Readings {
  private ctx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private timers: number[] = [];
  private levels: number[] = [];
  private state: DoorReadings = NO_READINGS;
  private stopped = false;

  constructor(private readonly changed: () => void) {}

  snapshot = (): DoorReadings => this.state;

  /**
   * Read these tracks; either may be missing. It is called again when somebody
   * picks another camera or microphone at the door, so the checks read what is
   * on now rather than what was on when the door opened.
   */
  start(video: MediaStreamTrack | null, audio: MediaStreamTrack | null): void {
    if (this.stopped) return;
    this.useCamera(video);
    this.useMic(audio);
    if (this.timers.length > 0) return;
    this.timers.push(window.setInterval(() => this.readLevel(), LEVEL_EVERY_MS));
    this.timers.push(window.setInterval(() => this.readLight(), LIGHT_EVERY_MS));
  }

  /** The camera or the microphone was turned off or on in the bar. */
  setOn(what: "camera" | "mic", on: boolean): void {
    if (what === "camera") this.patch({ camera: { ...this.state.camera, on } });
    else this.patch({ mic: { ...this.state.mic, on } });
  }

  /** Something refused to start, in plain words. */
  setProblem(what: "camera" | "mic", problem: string | null): void {
    if (what === "camera") this.patch({ camera: { ...this.state.camera, problem } });
    else this.patch({ mic: { ...this.state.mic, problem } });
  }

  /** The page may play and hear sound only after a click in it. */
  wake(): void {
    if (this.ctx?.state === "suspended") void this.ctx.resume();
  }

  stop(): void {
    this.stopped = true;
    for (const t of this.timers) window.clearInterval(t);
    this.timers = [];
    this.source?.disconnect();
    this.video?.pause();
    this.video = null;
    void this.ctx?.close().catch(() => {});
    this.ctx = null;
  }

  private useCamera(track: MediaStreamTrack | null): void {
    this.video?.pause();
    this.video = null;
    if (!track || typeof document === "undefined") return;
    const { width = 0, height = 0 } = track.getSettings();
    this.patch({ camera: { ...this.state.camera, width, height } });
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([track]);
    void video.play().catch(() => {});
    this.video = video;
    const canvas = document.createElement("canvas");
    canvas.width = LIGHT_SIZE;
    canvas.height = LIGHT_SIZE;
    this.canvas = canvas;
  }

  private useMic(track: MediaStreamTrack | null): void {
    this.source?.disconnect();
    this.source = null;
    if (!track || typeof AudioContext === "undefined") return;
    this.patch({ mic: { ...this.state.mic, label: track.label, silentMs: 0 } });
    // One audio context for the door, whichever microphone is on it.
    const ctx = this.ctx ?? new AudioContext();
    const analyser = this.analyser ?? ctx.createAnalyser();
    analyser.fftSize = 2048;
    this.source = ctx.createMediaStreamSource(new MediaStream([track]));
    this.source.connect(analyser);
    this.ctx = ctx;
    this.analyser = analyser;
  }

  private readLevel(): void {
    const analyser = this.analyser;
    if (!analyser) return;
    const samples = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(samples);
    const level = this.state.mic.on ? levelOf(samples) : 0;
    this.levels.push(level);
    if (this.levels.length > LEVELS_KEPT) this.levels.shift();
    const running = this.ctx?.state === "running";
    // Why only while on and running: a muted or not yet woken microphone reads silence that is not the device's.
    const silentMs = this.state.mic.on && running && level < SILENT_LEVEL ? this.state.mic.silentMs + LEVEL_EVERY_MS : 0;
    this.patch({
      mic: { ...this.state.mic, loudest: Math.max(...this.levels), needsClick: !running, silentMs },
    });
  }

  private readLight(): void {
    const video = this.video;
    const canvas = this.canvas;
    if (!video || !canvas || !this.state.camera.on || !video.videoWidth) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, LIGHT_SIZE, LIGHT_SIZE);
    this.patch({ brightness: brightnessOf(ctx.getImageData(0, 0, LIGHT_SIZE, LIGHT_SIZE).data) });
  }

  private patch(change: Partial<DoorReadings>): void {
    this.state = { ...this.state, ...change };
    this.changed();
  }
}
